/* Store Builder craft (15 Oct 2026): store-kit/README.md, "Craft hooks". No network, no markup from strings.
   Post-mortem, 19 Oct 2026 (Archie, on a phone: the gallery arrows were "more laggy than swiping (looks lower FPS)"):
   an arrow or thumbnail click ran this script's own eased scroll over Horizon's, set from the page's thread every
   frame with snap off, so every frame waited on that thread. Safari draws such frames at 60 a second while a finger's
   scroll runs at the screen's rate; in Chrome (phone, 4x CPU) 40ms tasks repeated 4 to 6 frames per tap, where a
   swipe and the browser's own smooth scroll repeated none. Rule: the kit never scrolls a gallery frame by frame; an
   arrow is Horizon's smooth scroll, and --sb-gallery-duration times only the fade. */
(() => {
const doc = document;
const root = doc.documentElement;
if (root.hasAttribute('data-sb-craft')) return;
root.setAttribute('data-sb-craft', '');

// Values, default first; or a default in ms (500 at most: the design review).
const HOOKS = {
  reveal: ['off', 'on'],
  'gallery-motion': ['slide', 'fade'],
  'gallery-duration': 450, // 300 for a fade
  'card-hover': ['crossfade', 'zoom', 'none'],
  'card-hover-duration': 350,
  'variant-style': ['boxed', 'underline'], // no pill: house rules §1
  'sticky-buy': ['off', 'on'], // off: house rules §5; store-builder-craft-sticky.js
  'popup-mark': ['none', 'plus', 'chevron'],
  'card-crop': ['top', 'center'],
  'drawer-utilities': ['on', 'off'],
  'header-scroll': ['layout', 'hide', 'stay'], // store-builder-craft-sticky.js
};
const media = (q) => Boolean(window.matchMedia?.(q).matches);
const still = media('(prefers-reduced-motion: reduce)');
const hasIO = 'IntersectionObserver' in window;
const later = (fn) => requestAnimationFrame(fn);
const once = (fn, rootMargin) => {
  const io = new IntersectionObserver((entries) => {
    for (const { isIntersecting, target } of entries) {
      if (isIntersecting) io.unobserve(target), fn(target);
    }
  }, { rootMargin });
  return io;
};

const hook = {};
const css = getComputedStyle(root);
for (const [name, known] of Object.entries(HOOKS)) {
  const raw = css.getPropertyValue(`--sb-${name}`).trim().toLowerCase();
  if (Array.isArray(known)) {
    hook[name] = known.includes(raw) ? raw : known[0];
  } else {
    const m = /^(\d+(?:\.\d+)?)(ms|s)$/.exec(raw);
    const fade = name === 'gallery-duration' && hook['gallery-motion'] === 'fade';
    hook[name] = m ? Math.round(Math.min(500, Number(m[1]) * (m[2] === 's' ? 1000 : 1))) : fade ? 300 : known;
    root.style.setProperty(`--sb-${name}`, `${hook[name]}ms`);
  }
  root.setAttribute(`data-sb-${name}`, String(hook[name]));
}

// Reveal: sections below the fold rise in once, their items staggered.
const ITEMS = 'h1,h2,h3,h4,p,img,video,blockquote,details,.button,.product-card,.collection-card,.resource-list__item';
function reveal() {
  if (hook.reveal !== 'on' || still || !hasIO || root.classList.contains('shopify-design-mode')) return;
  const io = once((section) => {
    section.setAttribute('data-sb-rise', 'in');
    setTimeout(() => section.removeAttribute('data-sb-rise'), 1500);
  }, '0px 0px -8% 0px');
  for (const section of doc.querySelectorAll('main .shopify-section')) {
    if (section.getBoundingClientRect().top < innerHeight) continue;
    const found = [...section.querySelectorAll(ITEMS)].slice(0, 40);
    const items = found.filter((a) => !found.some((b) => b !== a && b.contains(a))).slice(0, 12);
    if (!items.length) continue;
    items.forEach((item, i) => item.classList.add('sb-ri') || item.style.setProperty('--sb-i', String(i)));
    section.setAttribute('data-sb-rise', 'pending');
    io.observe(section);
  }
}

// Galleries: the shown photo and its neighbours load early.
const GALLERY = 'media-gallery slideshow-component';
const CARD = '.card-gallery slideshow-component';
const slidesOf = (show) => [...show.querySelectorAll('slideshow-slide')]
  .filter((s) => s.closest('slideshow-component') === show && (!s.hidden || s.hasAttribute('reveal')));
const offsetOf = (sc, slide) => slide.offsetLeft - (parseFloat(getComputedStyle(sc).paddingInlineStart) || 0);

function warm(slide) {
  for (const img of slide ? slide.querySelectorAll('img') : []) {
    img.setAttribute('loading', 'eager');
    img.decode?.().catch(() => {});
  }
}

// A far jump's slide sits next door until it lands: look again then.
function neighbours(show, slide) {
  const slides = slidesOf(show);
  const n = slides.length;
  if (!n) return;
  const at = slides.indexOf(slide);
  const i = Math.max(0, at >= 0 ? at : slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false'));
  for (const step of [0, 1, -1]) warm(slides[(i + step + n) % n]);
  show.style.setProperty('--sb-n', String(n));
  show.toggleAttribute('data-sb-progress', n > 1);
}

function progress(sc) {
  const max = sc.scrollWidth - sc.clientWidth;
  sc.closest('slideshow-component')?.style.setProperty('--sb-p', String(max > 0 ? sc.scrollLeft / max : 0));
}

// Slide: Horizon's own smooth scroll, left alone (the post-mortem at the top).

// A copy of a slide's photo over the slideshow: on all of it, or (placed) where the photo is.
function copyOf(box, slide, className, placed) {
  const img = slide?.querySelector('img');
  if (!img || !box) return null;
  const copy = Object.assign(doc.createElement('img'), { className, alt: '' });
  if (img.currentSrc) copy.src = img.currentSrc;
  else for (const at of ['srcset', 'sizes', 'src']) if (img.hasAttribute(at)) copy.setAttribute(at, img.getAttribute(at));
  copy.setAttribute('aria-hidden', 'true');
  const look = getComputedStyle(img);
  Object.assign(copy.style, { objectFit: look.objectFit, objectPosition: look.objectPosition });
  const [a, b] = placed ? [img, box].map((el) => el.getBoundingClientRect()) : [];
  if (a?.width > 0) Object.assign(copy.style, { inset: `${a.top - b.top}px auto auto ${a.left - b.left}px`, width: `${a.width}px`, height: `${a.height}px` });
  return copy;
}

// A card's crossfade: the leaving photo, laid over and faded out.
function ghost(show, slide, ms) {
  const box = show.querySelector('slideshow-container');
  const copy = copyOf(box, slide, 'sb-ghost');
  if (!copy) return;
  box.querySelector(':scope > .sb-ghost')?.remove();
  copy.style.setProperty('--sb-ms', `${ms}ms`);
  box.append(copy);
  setTimeout(() => copy.remove(), ms + 60);
}

// Fade through the gallery's ground: the leaving photo goes (80ms), then the arriving one fades in once
// drawn (a second at most). Never two photos at once.
function fade(show, sc, to, leaving, arriving, ms) {
  const box = show.querySelector('slideshow-container');
  const out = copyOf(box, leaving, 'sb-fade', true);
  sc.scrollTo({ left: to, behavior: 'instant' });
  const into = out && copyOf(box, arriving, 'sb-fade sb-fade-in', true);
  if (!into) return;
  for (const old of box.querySelectorAll(':scope > .sb-fade')) old.remove();
  const ground = Object.assign(doc.createElement('div'), { className: 'sb-fade sb-fade-ground' });
  const gone = Math.min(80, Math.round(ms / 3));
  into.style.setProperty('--sb-ms', `${ms - gone}ms`);
  into.style.setProperty('--sb-delay', `${gone}ms`);
  out.style.setProperty('--sb-ms', `${gone}ms`);
  box.append(ground, out);
  let go = () => {
    go = () => {};
    if (!out.isConnected) return;
    out.classList.add('sb-fade-out');
    box.append(into);
    setTimeout(() => [ground, out, into].forEach((el) => el.remove()), ms + 60);
  };
  Promise.resolve(into.decode?.()).then(() => go(), () => go());
  setTimeout(() => go(), 1000);
}

function enter(slide, ms) {
  slide.removeAttribute('data-sb-enter');
  void slide.offsetWidth; // restarts the animation
  slide.style.setProperty('--sb-ms', `${ms}ms`);
  slide.setAttribute('data-sb-enter', '');
  setTimeout(() => slide.removeAttribute('data-sb-enter'), ms + 60);
}

function onSelect({ target: show, detail = {} }) {
  if (!(show instanceof Element)) return;
  const gallery = show.matches(GALLERY) && !show.closest('dialog');
  if (gallery) {
    neighbours(show, detail.slide);
    setTimeout(() => neighbours(show, detail.slide), 1000);
  }
  const sc = show.querySelector('slideshow-slides');
  if (still || detail.trigger !== 'select' || !sc || !(detail.slide instanceof Element)) return;
  const to = offsetOf(sc, detail.slide);
  const moving = Math.abs(to - sc.scrollLeft) >= 2;
  if (gallery && moving && hook['gallery-motion'] === 'fade') {
    const leaving = slidesOf(show).find((s) => Math.abs(offsetOf(sc, s) - sc.scrollLeft) < 2);
    fade(show, sc, to, leaving, detail.slide, hook['gallery-duration']);
  } else if (!gallery && !moving && show.matches(CARD) && hook['card-hover'] !== 'none') {
    // Horizon swaps a card's photo at once; this crossfades it.
    const ms = hook['card-hover-duration'];
    ghost(show, slidesOf(show)[detail.previousIndex], ms);
    if (hook['card-hover'] === 'zoom') enter(detail.slide, ms);
  }
}

function galleries() {
  for (const show of doc.querySelectorAll(GALLERY)) neighbours(show);
  doc.addEventListener('slideshow:select', onSelect);
  let frame = 0;
  doc.addEventListener('scroll', ({ target }) => {
    if (!(target instanceof Element) || !target.matches('media-gallery slideshow-slides')) return;
    cancelAnimationFrame(frame);
    frame = later(() => progress(target));
  }, { capture: true, passive: true });
  // Cards: the second photo loads before the pointer arrives.
  if (!hasIO || !media('(hover: hover)')) return;
  const io = once((show) => warm(slidesOf(show)[1]), '200px');
  for (const show of doc.querySelectorAll(CARD)) io.observe(show);
}

reveal();
galleries();
})();
